import { ReactNode, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { DesktopSidebar } from "./DesktopSidebar";
import { MobileNav } from "@/components/MobileNav";
import { useAuth } from "@/contexts/AuthContext";
import { useCalendarAutoSync } from "@/hooks/useCalendarAutoSync";
import { useSubscription } from "@/hooks/useSubscription";
import { TrialRecapPaywall } from "@/components/paywall/TrialRecapPaywall";
import { FamilyScopeGuard } from "@/components/family/FamilyScopeGuard";
import { useFamilyScope } from "@/contexts/FamilyScopeContext";
import { useFamilyCapabilities } from "@/hooks/useFamilyCapabilities";
import { canUseNavKey, type FamilyNavKey } from "@/lib/familyNavigation";
import { useFamilyRouteFallback } from "@/hooks/useFamilyRouteFallback";
import type { TabType } from "@/types/navigation";

interface AppShellProps {
  children: ReactNode;
  /**
   * Pages that own their own sidebar/nav (e.g. Dashboard.tsx) can opt out
   * to avoid duplicate chrome.
   */
  disabled?: boolean;
  /** Suppress the trial recap upsell: the scan workspace is a working surface,
   *  and an upsell there blocks the tool the person came to use. */
  hideRecap?: boolean;
}

/**
 * Global authenticated app shell.
 * Renders the DesktopSidebar (lg+) and floating MobileNav (mobile/tablet)
 * on every signed-in route so Family, Fennie, Documents, Recurring, etc.
 * keep consistent navigation. Quick actions fall back to navigation:
 *   - Add Expense → /add
 *   - Scan Receipt → /scanner
 *   - Add to Goal → /goals
 *   - Upload Document → /documents
 */
export function AppShell({ children, disabled, hideRecap }: AppShellProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut } = useAuth();
  const { planType, isPremium, loading: subLoading, hadTrial, trialEndsAt, fetchError } = useSubscription();
  const { scope } = useFamilyScope();
  const caps = useFamilyCapabilities();
  const inFamilyScope = scope.type === 'family';
  // Which navigation entries the active view is allowed to show. Personal
  // scope always shows everything; a family scope shows only what the member's
  // role grants, so the two views genuinely look different.
  const can = (key: FamilyNavKey) => canUseNavKey(key, caps, inFamilyScope);
  useCalendarAutoSync();
  useFamilyRouteFallback();

  // Trial-expired hard gate: only shown when the user actually had a trial
  // that has now ended. Skipped when the subscription fetch itself failed,
  // so transient network errors don't slam brand-new users with a paywall.
  const dismissKey = user ? `fennest.trialRecap.dismissed:${user.id}` : null;
  const [recapDismissed, setRecapDismissed] = useState<boolean>(() => {
    if (typeof window === 'undefined' || !dismissKey) return true;
    return window.localStorage.getItem(dismissKey) === '1';
  });
  useEffect(() => {
    if (!dismissKey || typeof window === 'undefined') return;
    setRecapDismissed(window.localStorage.getItem(dismissKey) === '1');
  }, [dismissKey]);

  const trialActuallyEnded =
    hadTrial && !!trialEndsAt && new Date(trialEndsAt).getTime() <= Date.now();

  const shouldShowRecap =
    !!user &&
    !subLoading &&
    !fetchError &&
    !isPremium &&
    planType === 'free' &&
    trialActuallyEnded &&
    !recapDismissed &&
    !disabled &&
    !hideRecap;


  // Derive active tab from current pathname so nav highlights stay correct
  // on non-Dashboard pages.
  const deriveTab = (): TabType => {
    const p = location.pathname;
    if (p.startsWith("/budget")) return "budget";
    if (p.startsWith("/goals")) return "goals";
    if (p.startsWith("/calendar")) return "calendar";
    if (p.startsWith("/insights")) return "insights";
    if (p.startsWith("/settings")) return "settings";
    return "home";
  };

  const [activeTab, setActiveTab] = useState<TabType>(deriveTab());

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    const routes: Record<TabType, string> = {
      home: "/dashboard",
      budget: "/budget-details",
      goals: "/goals",
      calendar: "/calendar",
      insights: "/insights",
      settings: "/settings",
    };
    navigate(routes[tab]);
  };

  if (disabled) {
    // Dashboard owns its chrome and renders the blocked screen inside it, so a
    // member never loses the sidebar and the way back out.
    return <>{children}</>;
  }

  // FAB actions on non-Dashboard routes: hop to /dashboard and signal the
  // Dashboard's own drawer/sheet to open on arrival. This keeps every add
  // flow (including XP, discipline, dedup) in one place and avoids the
  // "took me home first, needed a second tap" UX bug.
  const handleAddExpense = () => navigate("/dashboard?add=expense");
  const handleScanReceipt = () => navigate("/dashboard?add=scan");
  const handleAddToGoal = () => navigate("/goals");
  const handleUploadDocument = () => navigate("/documents");

  return (
    <div className="min-h-screen bg-background w-full max-w-full overflow-x-hidden">
      <DesktopSidebar
        activeTab={activeTab}
        onTabChange={handleTabChange}
        onAddExpense={handleAddExpense}
        onScanReceipt={handleScanReceipt}
        onUploadDocument={handleUploadDocument}
        onSignOut={signOut}
        can={can}
      />

      <div className="lg:ps-64 w-full max-w-full overflow-x-hidden">
        {/* Reserve space for the floating pill on mobile/tablet */}
        <div className="pb-28 lg:pb-0 w-full max-w-full overflow-x-hidden">
          <FamilyScopeGuard>{children}</FamilyScopeGuard>
        </div>
      </div>


      <MobileNav
        activeTab={activeTab}
        onTabChange={handleTabChange}
        onAddExpense={handleAddExpense}
        onScanReceipt={handleScanReceipt}
        onAddToGoal={handleAddToGoal}
        can={can}
      />

      {shouldShowRecap && (
        <TrialRecapPaywall
          open
          origin="trial-expired"
          onDismiss={() => {
            if (dismissKey && typeof window !== 'undefined') {
              window.localStorage.setItem(dismissKey, '1');
            }
            setRecapDismissed(true);
          }}
        />
      )}
    </div>
  );
}
